import type { StoryLine } from '../../../packages/contracts/src/index.ts';
import { findCompanionVoice, type StoryContent, type StorySession } from '../../../packages/sim/src/campaign/story.ts';
import { storyPcId, storyText } from '../../../packages/sim/src/campaign/story-legacy.ts';

export type Expression = 'listening' | 'warm' | 'firm';

/** What this person has actually said and remembered; hiring buys no approval. */
export function companionPresence(content: StoryContent, story: StorySession, id: string, line?: StoryLine): null | {
  expression: Expression; obligation: string; boundary: string; memory: string | null; bearing: string;
} {
  const candidate = story.candidates.find(candidate => candidate.person.id === id);
  const person = story.slice.continuity.persons[id];
  if (!candidate || !person || (person.lifecycle !== 'active' && person.lifecycle !== 'dead')) return null;
  const survivor = candidate.templateId === 'survivor';
  const voice = survivor ? content.survivorVoice : findCompanionVoice(content, id);
  if (!voice) return null;
  const words = (text: string): string => storyText(story, text);
  const pc = storyPcId(story), relationships = person.current.relationships;
  const bargains = survivor ? content.survivorBargains : [];
  const rememberedWords = new Set([voice.moment.kept, voice.moment.broken,
    ...(survivor ? [content.narration.legacyPromiseKept, ...bargains.flatMap(bargain => [bargain.later.taken, bargain.later.refused])] : []),
  ].filter((text): text is string => !!text).map(words));
  const remembered = relationships.findLast(entry => entry.personId === pc && rememberedWords.has(entry.fact))?.fact;
  const obligationKey = `obligation:${id}`;
  const journal = story.journal.findLast(entry => [
    `${obligationKey}:request`, `${obligationKey}:kept`, `${obligationKey}:refused`, `companion:${id}`,
  ].includes(entry.id));
  const prefix = `${words(candidate.name)}: `;
  const recorded = journal?.text.startsWith(prefix) ? journal.text.slice(prefix.length) : journal?.text;
  const agreed = candidate.contract.agreedApproach;
  const accepted = agreed === 'guard' || agreed === 'engage' || agreed === 'withdraw' ? voice.discuss[agreed].accepted : null;
  const earnedKept = bargains.find(bargain => story.slice.quest.facts.includes(`legacy:took:${bargain.id}`)
    && bargain.later.keptBy && (story.slice.quest.facts.includes(bargain.later.keptBy) || story.completed.includes(bargain.later.keptBy)))?.later.kept;
  const refusal = candidate.contract.refusals.findLast(entry => entry.topic === 'approach:guard' || entry.topic === 'approach:engage' || entry.topic === 'approach:withdraw');
  const refusedGoal = refusal?.topic.slice('approach:'.length) as keyof typeof voice.discuss | undefined;
  const boundary = words(refusedGoal ? voice.discuss[refusedGoal].refused : voice.refusal);
  const priorEmployer = relationships.findLast(entry => entry.personId !== pc && (entry.fact === 'hired' || entry.fact === 'departed'))?.personId;
  const absent = !story.companionIds.includes(id) && priorEmployer !== undefined
    && relationships.some(entry => entry.personId === priorEmployer && entry.fact === 'departed');
  const judgment = story.judgments[id];
  const memory = judgment ? words(judgment) : earnedKept ? words(earnedKept) : remembered ?? recorded
    ?? (absent ? words(voice.returning.absent) : accepted ? words(accepted) : null);
  const departed = story.departed.includes(id) || absent || relationships.some(entry => entry.personId === pc && entry.fact === 'departed');
  const matches = (text: string | undefined): boolean => text !== undefined && (remembered === words(text) || judgment === words(text));
  const refused = candidate.contract.refusals.length > 0 || journal?.id === `${obligationKey}:refused`
    || matches(voice.moment.broken) || matches(voice.judgment.bad) || matches(voice.judgment.depart)
    || bargains.some(bargain => matches(bargain.later.refused));
  const kept = !!earnedKept || journal?.id === `${obligationKey}:kept` || matches(voice.moment.kept) || matches(voice.judgment.good)
    || (survivor && matches(content.narration.legacyPromiseKept)) || bargains.some(bargain => matches(bargain.later.kept));
  const condemned = judgment === words(voice.judgment.bad);
  let expression: Expression = condemned ? 'firm' : kept ? 'warm' : refused ? 'firm' : accepted ? 'warm' : 'listening';
  // A line's own answer outranks a persistent mood, but never a death or departure.
  const says = (textId: string, text: string | undefined): boolean => line?.speakerId === id
    && line.textId === textId && text !== undefined && line.text === words(text);
  for (const proposal of content.companionDiscussion) {
    const discussion = voice.discuss[proposal.goal], key = `story:${id}:${proposal.id}`;
    if (says(`${key}:accepted`, discussion.accepted)) expression = 'warm';
    for (const reason of ['danger-exceeds-temperament', 'obligation-forbids', 'boundary-non-negotiable', 'temperament-declines', 'already-refused']) {
      if (says(`${key}:${reason}`, discussion.refused)) expression = 'firm';
    }
  }
  if (says(`story:${id}:hire`, voice.hire) || says(`${obligationKey}:kept`, voice.moment.kept)) expression = 'warm';
  if (says('legacy:reaction', earnedKept)) expression = 'warm';
  if (says(`${obligationKey}:refused`, voice.moment.broken)
    || (line?.speakerId === id && line.text === words(voice.refusal))) expression = 'firm';
  if (says(`story:${id}:judgment`, voice.judgment.good)) expression = 'warm';
  if (says(`story:${id}:judgment`, voice.judgment.bad) || says(`story:${id}:judgment`, voice.judgment.depart)) expression = 'firm';
  const dead = person.lifecycle === 'dead';
  if (dead) expression = 'listening';
  else if (departed) expression = 'firm';
  const bearing = dead ? 'They will not answer again.' : departed ? 'They have walked away.'
    : expression === 'firm' ? 'They have already given you their answer.'
    : kept ? 'They remember the promise you kept.'
    : (story.paidCuts[id] ?? 0) > 0 ? 'The agreed wage has been paid.'
    : journal?.id === `${obligationKey}:request` ? 'Your promise is still between you.'
    : 'They turn to listen.';
  return { expression, obligation: words(voice.obligation), boundary, memory, bearing };
}
